import React, { useEffect, useState } from "react";
import { Link, useParams } from "react-router-dom";
import { ChevronLeft } from "lucide-react";
import api, { resolveImage } from "@/lib/api";
import SEO from "@/components/SEO";
import StarLoader from "@/components/StarLoader";

/** Blog post detail page — /blog/:slug */
export default function BlogPost() {
  const { slug } = useParams();
  const [post, setPost] = useState(null);
  const [notFound, setNotFound] = useState(false);

  useEffect(() => {
    setPost(null);
    setNotFound(false);
    api.get(`/blog/${slug}`)
      .then(({ data }) => setPost(data))
      .catch(() => setNotFound(true));
  }, [slug]);

  if (notFound) {
    return (
      <main className="mx-auto max-w-3xl px-5 sm:px-10 py-24 text-center" data-testid="blog-not-found">
        <SEO title="Post not found" noindex />
        <h1 className="font-display text-3xl font-light">Post not found</h1>
        <Link to="/blog" className="text-sj-goldDark underline underline-offset-4 mt-6 inline-block">← Back to Journal</Link>
      </main>
    );
  }
  if (!post) {
    return <div className="min-h-[60vh] flex items-center justify-center"><StarLoader label="Loading…" /></div>;
  }

  return (
    <main className="mx-auto max-w-3xl px-5 sm:px-10 py-14 sm:py-20" data-testid="blog-post">
      <SEO
        title={post.title}
        description={post.excerpt || post.content.slice(0, 180)}
        image={post.cover_image ? resolveImage(post.cover_image, { w: 1200 }) : undefined}
        path={`/blog/${post.slug}`}
        type="article"
        jsonLd={{
          "@context": "https://schema.org",
          "@type": "BlogPosting",
          headline: post.title,
          description: post.excerpt || post.content.slice(0, 180),
          image: post.cover_image ? resolveImage(post.cover_image, { w: 1200 }) : undefined,
          datePublished: post.published_at || post.created_at,
          dateModified: post.updated_at || post.published_at || post.created_at,
          author: { "@type": "Organization", name: "Star Jewels" },
          publisher: { "@type": "Organization", name: "Star Jewels" },
        }}
      />

      <Link to="/blog" className="inline-flex items-center gap-1.5 text-sm text-sj-inkSoft hover:text-sj-goldDark mb-8" data-testid="blog-back-link">
        <ChevronLeft size={14} /> Back to Journal
      </Link>

      <div className="text-[10px] tracking-[0.22em] uppercase text-sj-goldDark">
        {new Date(post.published_at || post.created_at).toLocaleDateString("en-IN", { day: "numeric", month: "long", year: "numeric" })}
      </div>
      <h1 className="mt-3 font-display text-3xl sm:text-5xl font-light leading-tight text-sj-ink" data-testid="blog-title">
        {post.title}
      </h1>

      {post.cover_image && (
        <img
          src={resolveImage(post.cover_image, { w: 1200 })}
          alt={`${post.title} — Star Jewels`}
          className="mt-8 w-full aspect-[16/9] object-cover rounded-md"
          loading="eager"
          decoding="async"
        />
      )}

      {/* Content — preserve line breaks and paragraphs from a simple textarea. */}
      <div className="mt-10 prose prose-lg max-w-none text-sj-ink" data-testid="blog-content">
        {post.content.split(/\n\s*\n/).map((para, i) => (
          <p key={i} className="mb-5 leading-relaxed text-[16px] text-sj-inkSoft whitespace-pre-line">
            {para.trim()}
          </p>
        ))}
      </div>

      {post.tags?.length > 0 && (
        <div className="mt-10 pt-6 border-t border-sj-ink/10 flex items-center gap-2 flex-wrap">
          <span className="text-xs text-sj-muted">Tags:</span>
          {post.tags.map((t) => (
            <span key={t} className="text-[11px] px-2.5 py-1 rounded-full bg-sj-cream text-sj-ink">{t}</span>
          ))}
        </div>
      )}
    </main>
  );
}
